import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

/* Full-page long-form article layout used by Case Studies and Insights */
export const ArticleLayout = ({ item, kicker, backTo, backLabel }) => {
  if (!item) {
    return (
      <div className="container-x flex min-h-[60vh] flex-col items-center justify-center pt-[72px] text-center">
        <h1 className="text-3xl font-semibold">Not found</h1>
        <Link to={backTo} className="mt-6 text-accent">{backLabel}</Link>
      </div>
    );
  }
  return (
    <article>
      {/* Full-bleed hero */}
      <section className="relative flex min-h-[72vh] items-end overflow-hidden pt-[72px]">
        <img src={item.hero} alt={item.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="container-x relative pb-16 text-white">
          <Reveal>
            <Link to={backTo} className="mb-8 inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-accent">
              <ArrowLeft className="h-4 w-4" /> {backLabel}
            </Link>
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-accent">
              {item.category || item.industry} · {kicker}
            </p>
            <h1 className="mt-5 max-w-4xl text-balance font-display text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl">
              {item.title}
            </h1>
            {(item.date || item.client) && (
              <p className="mt-5 text-sm text-white/60">
                {item.client}{item.client && item.date ? " · " : ""}{item.date}{item.read ? ` · ${item.read} read` : ""}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* Metrics strip */}
      {item.metrics && (
        <section className="border-b border-border bg-secondary/40">
          <div className="container-x grid grid-cols-3 gap-6 py-10">
            {item.metrics.map((m) => (
              <Reveal key={m.v} className="text-center sm:text-left">
                <p className="font-display text-3xl font-semibold text-gradient sm:text-4xl">{m.k}</p>
                <p className="mt-1 text-sm text-muted-foreground">{m.v}</p>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Lead */}
      <section className="bg-background pt-20">
        <div className="container-x max-w-3xl">
          <Reveal>
            <p className="text-balance text-2xl font-light leading-relaxed text-foreground sm:text-[28px]">
              {item.lead}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story sections */}
      <section className="bg-background py-16">
        <div className="container-x space-y-20">
          {item.sections?.map((s, i) => (
            <div key={i} className="grid items-center gap-10 lg:grid-cols-2">
              <Reveal className={i % 2 ? "lg:order-2" : ""}>
                <div className="overflow-hidden rounded-2xl border border-border shadow-xl">
                  <motion.img
                    src={s.image} alt={s.heading}
                    className="aspect-[4/3] w-full object-cover"
                    initial={{ scale: 1.1 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }}
                  />
                </div>
              </Reveal>
              <Reveal delay={0.1} className={i % 2 ? "lg:order-1" : ""}>
                <span className="font-mono text-sm text-accent">0{i + 1}</span>
                <h2 className="mt-3 text-balance text-3xl font-semibold tracking-tight">{s.heading}</h2>
                <p className="mt-5 text-lg font-light leading-relaxed text-muted-foreground">{s.body}</p>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-navy py-20 text-white grain">
        <div className="container-x relative text-center">
          <Reveal>
            <h2 className="mx-auto max-w-2xl text-balance text-3xl font-semibold tracking-tighter sm:text-4xl">
              Want results like these for your business?
            </h2>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full brand-gradient animate-gradient px-8 py-4 text-sm font-semibold brand-glow">
              Book a scoping call <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </article>
  );
};

export const useSlug = () => useParams().slug;
